import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useLocation, Link, useNavigate, useParams } from "react-router-dom";
import { AthleteTabMenu } from "./AthleteTabMenu";
import AthleteProfileCard from "../component/AthleteProfileCard";
import { RiDeleteBin5Line } from "react-icons/ri";
import { MdAdd } from "react-icons/md";

export const AdminAthleteWarmupRoutine = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();

  const [athlete, setAthlete] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [warmupTemplates, setWarmupTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAthleteData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL.GET_ATHLETE_BY_ID}/${athlete_id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          },
        );
        const data = await response.json();

        if (response.ok) {
          setAthlete(data);
        } else {
          setError(data.error || "Failed to load athlete data.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAthleteData();
  }, [athlete_id]);

  // Fetch all Warmup Templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch(
          `${API_URL.ADMIN_GET_SPEED_TEMPLATE_WARMUP_ROUTINE_LIST}`,
          {
            headers: { Authorization: `Bearer ${staff.token}` },
          },
        );
        const data = await res.json();
        console.log(data);
        if (res.ok) setWarmupTemplates(data.data);
        else setError(data.error || "Failed to load warmup templates.");
      } catch (err) {
        setError(err.message);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    const fetchAssignedRoutine = async () => {
      try {
        const res = await fetch(
          `${API_URL.GET_ATHLETE_WARMUP_ROUTINE}${athlete_id}`,
          {
            headers: { Authorization: `Bearer ${staff.token}` },
          },
        );
        const data = await res.json();
        if (res.ok && data?.warmup_template_id?._id) {
          setSelectedTemplateId(data.warmup_template_id._id);
          setMessage(`${data.warmup_template_id.template_title}`);
        }
      } catch (err) {
        console.error("Failed to fetch assigned warmup routine", err);
      }
    };

    fetchAssignedRoutine();
  }, [athlete_id]);

  // Submit assignment
  const handleAssignRoutine = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!selectedTemplateId) {
      setError("Please select a warmup routine.");
      return;
    }

    try {
      const response = await fetch(`${API_URL.SAVE_ATHLETE_WARMUP_ROUTINE}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({
          athlete_id,
          warmup_template_id: selectedTemplateId,
        }),
      });

      const data = await response.json();
      console.log(data);
      if (response.ok) {
        setMessage(`${data.template_title}`);
      } else {
        setError(data.error || "Failed to assign warmup routine.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    }
  };

  return (
    <div className="md:flex md:h-screen">
      <StaffSideMenu />
      <div className="md:flex-1 md:min-w-0 md:overflow-auto bg-bgBase max-md:pt-3 pb-5">
     
        {/* Top Header */}

        <div className="bg-white shadow sticky top-0 z-30">
          {/* <div className="w-full max-w-[1528px]"> */}
            {athlete && <AthleteProfileCard athlete={athlete} />}

            <AthleteTabMenu active="warmup-routine" />
          {/* </div> */}
        </div>

        {/* Assign Warmup Form */}
        <div className="pt-10 px-5 md:px-8 xl:px-16 space-y-3 md:space-y-6">
          <div className="bg-white border border-tblBorder p-5 md:p-8 rounded-14 w-full max-w-[1336px]">
            <h2 className="text-dark text-base font-inter font-medium mb-3">
              {message ? `Assigned: ${message}` : "Add Warmup Routine"}
            </h2>
            <div className="xl:w-1/3">
              <form onSubmit={handleAssignRoutine} className="w-full">
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="w-full outline-none bg-white border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight capitalize"
                >
                  <option value="">-- Select Warmup Routine --</option>
                  {warmupTemplates.map((template) => (
                    <option key={template._id} value={template._id}>
                      {template.template_title}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="w-full px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black flex items-center justify-center gap-3 mt-3"
                >
                  <MdAdd color="#fff" />
                  Assign to Athlete
                </button>
                {error && <p className="text-red-600 mt-2">{error}</p>}
              </form>
              {selectedTemplateId && (
                <button
                  type="button"
                  onClick={async () => {
                    setError("");
                    setMessage("");

                    const confirmed = window.confirm(
                      "Are you sure you want to delete the assigned warmup routine?",
                    );
                    if (!confirmed) return;

                    try {
                      const res = await fetch(
                        `${API_URL.DELETE_ATHLETE_WARMUP_ROUTINE}${athlete_id}`,
                        {
                          method: "DELETE",
                          headers: {
                            Authorization: `Bearer ${staff.token}`,
                          },
                        },
                      );

                      const data = await res.json();
                      if (res.ok) {
                        setMessage("");
                        setSelectedTemplateId("");
                      } else {
                        setError(data.error || "Failed to remove assignment.");
                      }
                    } catch (err) {
                      setError(err.message || "Something went wrong.");
                    }
                  }}
                  className="bg-bgBase w-full py-4 px-3 rounded-10 font-inter font-normal text-sm text-dark flex justify-between items-center gap-3 mt-3"
                >
                  Remove Assigned Routine <RiDeleteBin5Line color="#666" size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
